import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Layers, 
  Plus, 
  Edit2, 
  Trash2, 
  ArrowLeft,
  Building2,
  Eye,
  DollarSign,
  CheckCircle,
  XCircle,
  Settings,
  Calculator,
  LineChart,
  Briefcase,
  UserCog,
  Mail,
  Bot,
  Lock,
  Unlock
} from 'lucide-react';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0
  }).format(value || 0);
};

// Módulos base incluidos en el plan FinScope
const MODULOS_BASE = [
  'Dashboard', 'Movimientos', 'Categorías', 'Clientes', 
  'Flujo de Caja', 'Estado de Resultados', 'Pagos', 'Prioridades', 'Rentabilidad'
];

// Módulos Premium configuración
const MODULOS_PREMIUM = [
  { id: 'presupuestos', nombre: 'Presupuestos', icon: Calculator, precio: 500, descripcion: 'Planifica y controla gastos vs real' },
  { id: 'forecast', nombre: 'Forecast Financiero', icon: LineChart, precio: 800, descripcion: 'Proyecciones basadas en históricos' },
  { id: 'proyectos', nombre: 'Gestión de Proyectos', icon: Briefcase, precio: 600, descripcion: 'Control de proyectos por cliente' },
  { id: 'equipo', nombre: 'Gestión de Equipo', icon: UserCog, precio: 500, descripcion: 'Colaboradores y asignaciones' },
  { id: 'reportes_auto', nombre: 'Reportes Automáticos', icon: Mail, precio: 400, descripcion: 'Envío programado por correo' },
  { id: 'ai_advisor', nombre: 'AI Advisor', icon: Bot, precio: 1000, descripcion: 'Asistente financiero con IA' },
];

export default function AdminMarcas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { empresaId } = useParams();
  const [loading, setLoading] = useState(true);
  const [marcas, setMarcas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [selectedEmpresa, setSelectedEmpresa] = useState(empresaId || '');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modulosDialogOpen, setModulosDialogOpen] = useState(false);
  const [selectedMarcaForModulos, setSelectedMarcaForModulos] = useState(null);
  const [modulosPremium, setModulosPremium] = useState([]);
  const [editingMarca, setEditingMarca] = useState(null);
  const [formData, setFormData] = useState({
    empresa_id: empresaId || '',
    nombre_marca: '',
    descripcion: '',
    plan: 'finscope',
    precio_mensual: 4000,
    activo: true
  });

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      navigate('/dashboard');
      return;
    }
    loadData();
  }, [user, navigate, selectedEmpresa]);

  const loadData = async () => {
    try {
      const empresasRes = await api.get('/empresas');
      setEmpresas(empresasRes.data);
      
      let url = '/marcas';
      if (selectedEmpresa) {
        url += `?empresa_id=${selectedEmpresa}`;
      }
      const marcasRes = await api.get(url);
      setMarcas(marcasRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      empresa_id: selectedEmpresa || '',
      nombre_marca: '',
      descripcion: '',
      plan: 'finscope',
      precio_mensual: 4000,
      activo: true
    });
    setEditingMarca(null);
  };

  const openModulosDialog = async (marca) => {
    setSelectedMarcaForModulos(marca);
    try {
      const response = await api.get(`/marca/${marca.id}/modulos-premium`);
      setModulosPremium(response.data);
    } catch (error) {
      toast.error('Error al cargar módulos');
    }
    setModulosDialogOpen(true);
  };

  const toggleModuloPremium = async (moduloId, activo) => {
    try {
      await api.put(`/marca/${selectedMarcaForModulos.id}/modulos-premium/${moduloId}?activo=${activo}`);
      // Update local state
      setModulosPremium(prev => prev.map(m => 
        m.id === moduloId ? { ...m, activo } : m
      ));
      toast.success(`Módulo ${activo ? 'activado' : 'desactivado'}`);
    } catch (error) {
      toast.error('Error al actualizar módulo');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.empresa_id) {
      toast.error('Selecciona una empresa');
      return;
    }
    try {
      if (editingMarca) {
        await api.put(`/marcas/${editingMarca.id}`, formData);
        toast.success('Marca actualizada');
      } else {
        await api.post('/marcas', formData);
        toast.success('Marca creada');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Error al guardar marca');
    }
  };

  const handleEdit = (marca) => {
    setEditingMarca(marca);
    setFormData({
      empresa_id: marca.empresa_id,
      nombre_marca: marca.nombre_marca,
      descripcion: marca.descripcion || '',
      plan: marca.plan || 'basico',
      precio_mensual: marca.precio_mensual || 5000,
      activo: marca.activo
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta marca?')) return;
    try {
      await api.delete(`/marcas/${id}`);
      toast.success('Marca eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleEnterMarca = async (marca) => {
    try {
      // Find the cliente associated with this empresa
      // The empresa_nombre should match the cliente nombre
      const clientesRes = await api.get('/clientes');
      const clientes = clientesRes.data;
      
      // Find cliente by empresa name (the empresa is the main client)
      const empresaCliente = clientes.find(c => c.nombre === marca.empresa_nombre);
      
      if (empresaCliente) {
        // Save marca and cliente context for data scoping
        localStorage.setItem('selectedMarcaId', marca.id);
        localStorage.setItem('selectedMarcaNombre', marca.nombre_marca);
        localStorage.setItem('selectedClienteId', empresaCliente.id);
        localStorage.setItem('selectedClienteNombre', empresaCliente.nombre);
        navigate('/dashboard');
      } else {
        toast.error('No se encontró el cliente asociado a esta marca');
      }
    } catch (error) {
      toast.error('Error al entrar a la marca');
    }
  };

  return (
    <div data-testid="admin-marcas">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-mobile">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 style={{ fontFamily: 'Outfit, sans-serif' }}>Marcas</h1>
              <p className="text-xs text-muted-foreground">Gestión de marcas y planes</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" onClick={resetForm}>
                <Plus className="w-4 h-4 mr-1" />
                Nueva
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingMarca ? 'Editar Marca' : 'Nueva Marca'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Empresa *</Label>
                  <Select 
                    value={formData.empresa_id} 
                    onValueChange={(v) => setFormData({...formData, empresa_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona empresa" />
                    </SelectTrigger>
                    <SelectContent>
                      {empresas.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id}>
                          {emp.nombre_empresa}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nombre de la Marca *</Label>
                  <Input
                    value={formData.nombre_marca}
                    onChange={(e) => setFormData({...formData, nombre_marca: e.target.value})}
                    placeholder="Ej: Concept Haus"
                    required
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <Input
                    value={formData.descripcion}
                    onChange={(e) => setFormData({...formData, descripcion: e.target.value})}
                    placeholder="Breve descripción"
                  />
                </div>
                <div>
                  <Label>Plan</Label>
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-700">FinScope</Badge>
                      <span className="text-xs text-muted-foreground">
                        ({MODULOS_BASE.length} módulos base incluidos)
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Módulos incluidos */}
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-2">Módulos base incluidos:</p>
                  <div className="flex flex-wrap gap-1">
                    {MODULOS_BASE.map((mod) => (
                      <Badge key={mod} variant="secondary" className="text-xs">
                        <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                        {mod}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Los módulos premium se activan después de crear la marca.
                  </p>
                </div>

                <div>
                  <Label>Iguala Mensual (MXN) *</Label>
                  <Input
                    type="number"
                    value={formData.precio_mensual}
                    onChange={(e) => setFormData({...formData, precio_mensual: parseFloat(e.target.value) || 0})}
                    min={0}
                    placeholder="Ej: 15000"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Define el monto de la iguala acordada con el cliente
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="activo"
                    checked={formData.activo}
                    onChange={(e) => setFormData({...formData, activo: e.target.checked})}
                    className="rounded"
                  />
                  <Label htmlFor="activo">Marca activa</Label>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancelar
                  </Button>
                  <Button type="submit" className="flex-1">
                    {editingMarca ? 'Guardar' : 'Crear'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="page-content">
        {/* Filtro por empresa */}
        <div className="mb-4">
          <Select value={selectedEmpresa || "all"} onValueChange={(v) => setSelectedEmpresa(v === "all" ? "" : v)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Todas las empresas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las empresas</SelectItem>
              {empresas.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.nombre_empresa}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => (
              <div key={i} className="h-28 bg-slate-200 rounded-lg animate-pulse"></div>
            ))}
          </div>
        ) : marcas.length === 0 ? (
          <div className="text-center py-12">
            <Layers className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Sin marcas</h3>
            <p className="text-muted-foreground mb-4">Crea la primera marca para esta empresa</p>
            <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
              <Plus className="w-4 h-4 mr-2" />
              Crear Marca
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {marcas.map((marca) => (
              <Card key={marca.id} className={!marca.activo ? 'opacity-60' : ''}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold">{marca.nombre_marca}</h3>
                        <Badge className="bg-blue-100 text-blue-700">FinScope</Badge>
                        {!marca.activo && <Badge variant="secondary">Inactiva</Badge>}
                        {marca.modulos_premium?.length > 0 && (
                          <Badge className="bg-amber-100 text-amber-700">
                            +{marca.modulos_premium.length} Premium
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {marca.empresa_nombre} • {marca.descripcion || 'Sin descripción'}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="font-bold text-green-600">
                          {formatCurrency(marca.precio_mensual)}/mes
                        </span>
                      </div>
                      
                      {/* Módulos Base */}
                      <div className="mt-3 flex flex-wrap gap-1">
                        {MODULOS_BASE.slice(0, 4).map((mod) => (
                          <Badge key={mod} variant="outline" className="text-xs">
                            {mod}
                          </Badge>
                        ))}
                        <Badge variant="outline" className="text-xs">
                          +{MODULOS_BASE.length - 4} más
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 ml-2">
                      <Button 
                        size="sm"
                        onClick={() => handleEnterMarca(marca)}
                        data-testid={`enter-marca-${marca.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Entrar
                      </Button>
                      <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => openModulosDialog(marca)}
                        data-testid={`modulos-marca-${marca.id}`}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Módulos
                      </Button>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(marca)}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(marca.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Dialog para gestionar módulos premium */}
      <Dialog open={modulosDialogOpen} onOpenChange={setModulosDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Módulos Premium - {selectedMarcaForModulos?.nombre_marca}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Activa o desactiva los módulos premium para esta marca. Los módulos activos se mostrarán en el menú del usuario.
            </p>
            
            <div className="space-y-3">
              {MODULOS_PREMIUM.map((modulo) => {
                const isActivo = modulosPremium.find(m => m.id === modulo.id)?.activo || false;
                const Icon = modulo.icon;
                
                return (
                  <div 
                    key={modulo.id}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      isActivo ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${isActivo ? 'bg-green-100' : 'bg-slate-200'}`}>
                        <Icon className={`w-5 h-5 ${isActivo ? 'text-green-600' : 'text-slate-500'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{modulo.nombre}</p>
                        <p className="text-xs text-muted-foreground">{modulo.descripcion}</p>
                        <p className="text-xs text-blue-600 font-medium mt-1">
                          {formatCurrency(modulo.precio)}/mes
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={isActivo}
                      onCheckedChange={(checked) => toggleModuloPremium(modulo.id, checked)}
                    />
                  </div>
                );
              })}
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => { setModulosDialogOpen(false); loadData(); }}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
